import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { createUser, deleteUser, getModel, listCities, listUsers, updateModel, updateUser } from '../../../services/UserService';
import './Users.css';

const defaultModelParameters = { height: '', weight: '', body: '', hair: '', eyes: '', breast: '' };

const getUserId = (user) => user.uuid || user.id;
const getUserDisplayId = (user) => user.uuid ? user.uuid.slice(0, 8) : user.id;
const getLinkedModelUuid = (user) => typeof user?.model === 'object'
    ? user.model?.uuid || user.model?.id || ''
    : user?.model || '';
const getUserRoleLabel = (role) => {
    const normalizedRole = String(role || '').toLowerCase();
    if(Number(role) === 9 || normalizedRole === 'admin') return 'Admin';
    if(Number(role) === 2 || normalizedRole === 'model') return 'Model';
    if(Number(role) === 1 || normalizedRole === 'account' || normalizedRole === 'user') return 'User';
    return role || 'Unknown';
};

const Users = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [usersError, setUsersError] = useState('');
    const [cities, setCities] = useState([]);

    useEffect(() => {
        let isMounted = true;

        const loadUsers = async () => {
            try {
                const result = await listUsers();
                if(isMounted) setUsers(result.items);
            } catch (loadError) {
                if(isMounted) {
                    setUsersError(loadError instanceof TypeError ? 'Unable to connect to the users service.' : loadError.message);
                }
            } finally {
                if(isMounted) setIsLoadingUsers(false);
            }
        };

        loadUsers();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        listCities().then(items => {
            if(isMounted) setCities(items);
        }).catch(() => {
            if(isMounted) setCities([]);
        });
        return () => {
            isMounted = false;
        };
    }, []);

    const [showModal, setShowModal] = useState(false);
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 9,
        status: 'Active',
        country: 'IT',
        modelName: '',
        modelSlug: ''
    });
    const [modelCity, setModelCity] = useState('');
    const [modelParameters, setModelParameters] = useState(defaultModelParameters);
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, user: null });
    const [isDeletingUser, setIsDeletingUser] = useState(false);
    const [deleteUserError, setDeleteUserError] = useState('');
    
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [createUserError, setCreateUserError] = useState('');
    const [cityRates, setCityRates] = useState([
        { id: 1, duration: '30 Minutes', incall: '€300', outcall: 'N/A' },
        { id: 2, duration: '1 Hour', incall: '€500', outcall: '€550' },
    ]);
    
    const [travelRates, setTravelRates] = useState([
        { id: 1, duration: '24 Hours', incall: '€3000', outcall: '€3500' },
    ]);

    const [editingUser, setEditingUser] = useState(null);
    const [isLoadingEditModel, setIsLoadingEditModel] = useState(false);
    const [isSavingEditUser, setIsSavingEditUser] = useState(false);
    const [editUserError, setEditUserError] = useState('');
    const [editCityRate, setEditCityRate] = useState(null);
    const [editTravelRate, setEditTravelRate] = useState(null);
    const [showAddCityRate, setShowAddCityRate] = useState(false);
    const [showAddTravelRate, setShowAddTravelRate] = useState(false);
    const [newRate, setNewRate] = useState({ duration: '', incall: '', outcall: '' });

    useEffect(() => {
        if (searchParams.get('action') !== 'create') return;
        setCreateUserError('');
        setShowModal(true);
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('action');
        setSearchParams(nextSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleCreateUser = async () => {
        setCreateUserError('');
        if (!newUser.name || !newUser.email || !newUser.password) {
            setCreateUserError('Name, email, and password are required.');
            return;
        }
        if (newUser.password !== newUser.confirmPassword) {
            setCreateUserError('Passwords do not match.');
            return;
        }
        if (newUser.role === 2 && (!newUser.modelName || !modelCity)) {
            setCreateUserError('Model name and city are required for a model user.');
            return;
        }

        setIsCreatingUser(true);
        try {
            const createdUser = await createUser({
                ...newUser,
                ...(newUser.role === 2 ? {
                    model: {
                        name: newUser.modelName,
                        slug: newUser.modelSlug.trim(),
                        city: modelCity,
                        status: 'Inactive',
                        locationType: 'single',
                        singleCity: modelCity,
                        parameters: modelParameters,
                        cityRates,
                        travelRates
                    }
                } : {})
            });
            setUsers(currentUsers => [...currentUsers, createdUser]);
            setNewUser({ name: '', email: '', password: '', confirmPassword: '', role: 9, status: 'Active', country: 'IT', modelName: '', modelSlug: '' });
            setModelCity('');
            setModelParameters(defaultModelParameters);
            setShowModal(false);
        } catch (createError) {
            setCreateUserError(createError instanceof TypeError ? 'Unable to connect to the users service.' : createError.message);
        } finally {
            setIsCreatingUser(false);
        }
    };

    const handleEditUser = async (user) => {
        setEditUserError('');
        setEditingUser(user);
        const modelUuid = getLinkedModelUuid(user);
        if (Number(user.role) !== 2 || !modelUuid) return;
        setIsLoadingEditModel(true);
        try {
            const modelDetails = await getModel(modelUuid);
            setEditingUser(currentUser => currentUser && getUserId(currentUser) === getUserId(user)
                ? { ...currentUser, modelDetails }
                : currentUser);
        } catch (loadError) {
            setEditUserError(loadError instanceof TypeError ? 'Unable to connect to the users service.' : loadError.message);
        } finally {
            setIsLoadingEditModel(false);
        }
    };

    const handleSaveEditUser = async () => {
        if (!editingUser || !editingUser.name || !editingUser.email) return;
        setEditUserError('');
        setIsSavingEditUser(true);
        try {
            const updatedUser = await updateUser(getUserId(editingUser), {
                name: editingUser.name,
                email: editingUser.email,
                role: Number(editingUser.role),
                status: editingUser.status,
                country: editingUser.country || 'IT'
            });
            let savedUser = updatedUser;
            const modelUuid = getLinkedModelUuid(editingUser);
            if (Number(editingUser.role) === 2 && modelUuid && editingUser.modelDetails) {
                await updateModel(modelUuid, {
                    name: editingUser.modelDetails.name,
                    city: editingUser.modelDetails.city,
                    slug: editingUser.modelDetails.slug,
                    status: editingUser.modelDetails.status,
                    description: editingUser.modelDetails.description,
                    image: editingUser.modelDetails.image,
                    photos: editingUser.modelDetails.photos,
                    parameters: editingUser.modelDetails.parameters,
                    cityRates: editingUser.modelDetails.cityRates,
                    travelRates: editingUser.modelDetails.travelRates,
                    locationType: editingUser.modelDetails.locationType,
                    singleCity: editingUser.modelDetails.singleCity
                });
                savedUser = { ...updatedUser, model: modelUuid };
            }
            setUsers(currentUsers => currentUsers.map(user => getUserId(user) === getUserId(editingUser) ? savedUser : user));
            setEditingUser(null);
        } catch (saveError) {
            setEditUserError(saveError instanceof TypeError ? 'Unable to connect to the users service.' : saveError.message);
        } finally {
            setIsSavingEditUser(false);
        }
    };

    const handleDeleteUser = (user) => {
        setDeleteUserError('');
        setDeleteConfirm({ show: true, user });
    };

    const confirmDeleteUser = async () => {
        if (!deleteConfirm.user) return;
        const userToDelete = deleteConfirm.user;
        setDeleteUserError('');
        setIsDeletingUser(true);
        try {
            await deleteUser(getUserId(userToDelete));
            setUsers(currentUsers => currentUsers.filter(user => getUserId(user) !== getUserId(userToDelete)));
            setDeleteConfirm({ show: false, user: null });
        } catch (deleteError) {
            setDeleteUserError(deleteError instanceof TypeError ? 'Unable to connect to the users service.' : deleteError.message);
        } finally {
            setIsDeletingUser(false);
        }
    };

    const cancelDeleteUser = () => {
        setDeleteUserError('');
        setDeleteConfirm({ show: false, user: null });
    };

    const handleDeleteCityRate = (id) => {
        setCityRates(cityRates.filter(rate => rate.id !== id));
    };

    const handleEditCityRate = (id) => {
        const rate = cityRates.find(r => r.id === id);
        setEditCityRate(rate);
    };

    const handleSaveCityRate = () => {
        if (editCityRate) {
            setCityRates(cityRates.map(r => r.id === editCityRate.id ? editCityRate : r));
            setEditCityRate(null);
        }
    };

    const handleAddCityRate = () => {
        setShowAddCityRate(true);
        setNewRate({ duration: '', incall: '', outcall: '' });
    };

    const handleSaveNewCityRate = () => {
        if (newRate.duration && newRate.incall) {
            const newId = cityRates.length > 0 ? Math.max(...cityRates.map(r => r.id)) + 1 : 1;
            setCityRates([
                ...cityRates,
                { 
                    id: newId, 
                    duration: newRate.duration,
                    incall: newRate.incall.startsWith('€') ? newRate.incall : `€${newRate.incall}`,
                    outcall: newRate.outcall ? (newRate.outcall.startsWith('€') ? newRate.outcall : `€${newRate.outcall}`) : 'N/A'
                }
            ]);
            setShowAddCityRate(false);
            setNewRate({ duration: '', incall: '', outcall: '' });
        }
    };

    const handleDeleteTravelRate = (id) => {
        setTravelRates(travelRates.filter(rate => rate.id !== id));
    };

    const handleEditTravelRate = (id) => {
        const rate = travelRates.find(r => r.id === id);
        setEditTravelRate(rate);
    };

    const handleSaveTravelRate = () => {
        if (editTravelRate) {
            setTravelRates(travelRates.map(r => r.id === editTravelRate.id ? editTravelRate : r));
            setEditTravelRate(null);
        }
    };

    const handleAddTravelRate = () => {
        setShowAddTravelRate(true);
        setNewRate({ duration: '', incall: '', outcall: '' });
    };

    const handleSaveNewTravelRate = () => {
        if (newRate.duration && newRate.incall) {
            const newId = travelRates.length > 0 ? Math.max(...travelRates.map(r => r.id)) + 1 : 1;
            setTravelRates([
                ...travelRates,
                { 
                    id: newId, 
                    duration: newRate.duration,
                    incall: newRate.incall.startsWith('€') ? newRate.incall : `€${newRate.incall}`,
                    outcall: newRate.outcall ? (newRate.outcall.startsWith('€') ? newRate.outcall : `€${newRate.outcall}`) : `€${parseInt(newRate.incall) + 500}`
                }
            ]);
            setShowAddTravelRate(false);
            setNewRate({ duration: '', incall: '', outcall: '' });
        }
    };

    return (
        <div className="users">
            <NavigationMenu />
            <div className="users-content">
                <header className="users-header">
                    <h1 className="users-title">Users</h1>
                    <p className="users-subtitle">Manage admin users and permissions</p>
                </header>
                <main className="users-main">
                    <div className="table-container">
                        <div className="table-header">
                            <h2 className="table-title">System Users</h2>
                            <button className="add-button" onClick={() => { setCreateUserError(''); setShowModal(true); }}>Create User</button>
                        </div>
                        <div className="table-scroll">
                            <table className="data-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingUsers && (
                                    <tr>
                                        <td colSpan="6" className="users-feedback">Loading users...</td>
                                    </tr>
                                )}
                                {!isLoadingUsers && usersError && (
                                    <tr>
                                        <td colSpan="6" className="users-feedback users-error" role="alert">{usersError}</td>
                                    </tr>
                                )}
                                {!isLoadingUsers && !usersError && users.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="users-feedback">No users found.</td>
                                    </tr>
                                )}
                                {!isLoadingUsers && !usersError && users.map(user => (
                                    <tr key={getUserId(user)}>
                                        <td title={user.uuid || undefined}>#{getUserDisplayId(user)}</td>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td>{getUserRoleLabel(user.role)}</td>
                                        <td>
                                            <span className={`status-badge ${(user.status || 'unknown').toLowerCase()}`}>
                                                {user.status || 'Unknown'}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="action-btn edit" onClick={() => handleEditUser(user)}>Edit</button>
                                            <button className="action-btn delete" onClick={() => handleDeleteUser(user)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            </table>
                        </div>
                    </div>
                </main>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className={newUser.role === 2 ? 'modal extended' : 'modal'}>
                        <div className="modal-header">
                            <h3 className="modal-title">Create New User</h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-content">
                            <div className="modal-main">
                                <div className="form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text"
                                        value={newUser.name}
                                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                        placeholder="Enter full name"
                                        className="modal-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        value={newUser.email}
                                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                        placeholder="Enter email address"
                                        className="modal-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={newUser.password}
                                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                                        placeholder="Enter password"
                                        className="modal-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Confirm Password</label>
                                    <input
                                        type="password"
                                        value={newUser.confirmPassword}
                                        onChange={(e) => setNewUser({...newUser, confirmPassword: e.target.value})}
                                        placeholder="Confirm password"
                                        className="modal-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Role</label>
                                    <select
                                        value={newUser.role}
                                        onChange={(e) => setNewUser({...newUser, role: Number(e.target.value)})}
                                        className="modal-select"
                                    >
                                        <option value={9}>Admin</option>
                                        <option value={2}>Model</option>
                                        <option value={1}>User</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={newUser.status}
                                        onChange={(e) => setNewUser({...newUser, status: e.target.value})}
                                        className="modal-select"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Country</label>
                                    <select
                                        value={newUser.country}
                                        onChange={(e) => setNewUser({...newUser, country: e.target.value})}
                                        className="modal-select"
                                    >
                                        <option value="IT">Italy</option>
                                    </select>
                                </div>
                                {createUserError && <span className="modal-error" role="alert">{createUserError}</span>}
                            </div>
                            
                            {newUser.role === 2 && (
                                <div className="model-sidebar">
                                    <div className="sidebar-section">
                                        <h4 className="sidebar-title">Model Parameters</h4>
                                        <div className="form-group">
                                            <label>Model Name</label>
                                            <input
                                                type="text"
                                                value={newUser.modelName}
                                                onChange={(e) => setNewUser({...newUser, modelName: e.target.value})}
                                                placeholder="Enter model name"
                                                className="modal-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Profile Slug</label>
                                            <input
                                                type="text"
                                                value={newUser.modelSlug}
                                                onChange={(e) => setNewUser({...newUser, modelSlug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')})}
                                                placeholder="profile-slug (optional)"
                                                className="modal-input"
                                                maxLength="191"
                                            />
                                            <small>Used in the public profile URL. Leave blank to generate it from the model name.</small>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group half">
                                                <label>Height (cm)</label>
                                                <input type="number" className="modal-input" placeholder="165" value={modelParameters.height} onChange={(e) => setModelParameters({...modelParameters, height: e.target.value})} />
                                            </div>
                                            <div className="form-group half">
                                                <label>Weight (kg)</label>
                                                <input type="number" className="modal-input" placeholder="50" value={modelParameters.weight} onChange={(e) => setModelParameters({...modelParameters, weight: e.target.value})} />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group half">
                                                <label>Body (cm)</label>
                                                <input type="text" className="modal-input" placeholder="36-34-36" value={modelParameters.body} onChange={(e) => setModelParameters({...modelParameters, body: e.target.value})} />
                                            </div>
                                            <div className="form-group half">
                                                <label>Hair</label>
                                                <input type="text" className="modal-input" placeholder="black" value={modelParameters.hair} onChange={(e) => setModelParameters({...modelParameters, hair: e.target.value})} />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group half">
                                                <label>Eyes</label>
                                                <input type="text" className="modal-input" placeholder="green" value={modelParameters.eyes} onChange={(e) => setModelParameters({...modelParameters, eyes: e.target.value})} />
                                            </div>
                                            <div className="form-group half">
                                                <label>Breast</label>
                                                <input type="text" className="modal-input" placeholder="DD" value={modelParameters.breast} onChange={(e) => setModelParameters({...modelParameters, breast: e.target.value})} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sidebar-section">
                                        <h4 className="sidebar-title">Location</h4>
                                        <div className="form-group">
                                            <select className="modal-select" value={modelCity} onChange={(e) => setModelCity(e.target.value)}>
                                                <option value="">Select City</option>
                                                {cities.map(city => <option key={city.uuid} value={city.uuid}>{city.name}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="sidebar-section">
                                        <h4 className="sidebar-title">City Rates</h4>
                                        <div className="rates-table">
                                            <div className="rates-header">
                                                <div className="rate-cell">Duration</div>
                                                <div className="rate-cell">Incall</div>
                                                <div className="rate-cell">Outcall</div>
                                                <div className="rate-cell"></div>
                                            </div>
                                            {cityRates.map(rate => (
                                                <div key={rate.id} className="rates-row">
                                                    <div className="rate-cell">{rate.duration}</div>
                                                    <div className="rate-cell">{rate.incall}</div>
                                                    <div className="rate-cell">{rate.outcall}</div>
                                                    <div className="rate-cell">
                                                        <button className="rate-action" onClick={() => handleEditCityRate(rate.id)}>✎</button>
                                                        <button className="rate-action" onClick={() => handleDeleteCityRate(rate.id)}>×</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <button className="add-rate-button" onClick={handleAddCityRate}>Add Rate</button>
                                    </div>

                                    <div className="sidebar-section">
                                        <h4 className="sidebar-title">Travel Rates</h4>
                                        <div className="rates-table">
                                            <div className="rates-header">
                                                <div className="rate-cell">Duration</div>
                                                <div className="rate-cell">Incall</div>
                                                <div className="rate-cell">Outcall</div>
                                                <div className="rate-cell"></div>
                                            </div>
                                            {travelRates.map(rate => (
                                                <div key={rate.id} className="rates-row">
                                                    <div className="rate-cell">{rate.duration}</div>
                                                    <div className="rate-cell">{rate.incall}</div>
                                                    <div className="rate-cell">{rate.outcall}</div>
                                                    <div className="rate-cell">
                                                        <button className="rate-action" onClick={() => handleEditTravelRate(rate.id)}>✎</button>
                                                        <button className="rate-action" onClick={() => handleDeleteTravelRate(rate.id)}>×</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <button className="add-rate-button" onClick={handleAddTravelRate}>Add Rate</button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setShowModal(false)}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={handleCreateUser} disabled={isCreatingUser}>
                                {isCreatingUser ? 'Creating...' : 'Create User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit City Rate Modal */}
            {editCityRate && (
                <div className="modal-overlay">
                    <div className="modal small">
                        <div className="modal-header">
                            <h3 className="modal-title">Edit City Rate</h3>
                            <button className="modal-close" onClick={() => setEditCityRate(null)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Duration</label>
                                <select
                                    value={editCityRate.duration}
                                    onChange={(e) => setEditCityRate({...editCityRate, duration: e.target.value})}
                                    className="modal-select"
                                >
                                    <option value="30 Minutes">30 Minutes</option>
                                    <option value="1 Hour">1 Hour</option>
                                    <option value="2 Hour">2 Hour</option>
                                    <option value="3 Hour">3 Hour</option>
                                    <option value="4 Hour">4 Hour</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Incall Price (€)</label>
                                <input
                                    type="text"
                                    value={editCityRate.incall.replace('€', '')}
                                    onChange={(e) => setEditCityRate({...editCityRate, incall: `€${e.target.value}`})}
                                    className="modal-input"
                                    placeholder="300"
                                />
                            </div>
                            <div className="form-group">
                                <label>Outcall Price (€)</label>
                                <input
                                    type="text"
                                    value={editCityRate.outcall === 'N/A' ? '' : editCityRate.outcall.replace('€', '')}
                                    onChange={(e) => setEditCityRate({...editCityRate, outcall: e.target.value ? `€${e.target.value}` : 'N/A'})}
                                    className="modal-input"
                                    placeholder="350"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setEditCityRate(null)}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={handleSaveCityRate}>
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add City Rate Modal */}
            {showAddCityRate && (
                <div className="modal-overlay">
                    <div className="modal small">
                        <div className="modal-header">
                            <h3 className="modal-title">Add City Rate</h3>
                            <button className="modal-close" onClick={() => setShowAddCityRate(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Duration</label>
                                <select
                                    value={newRate.duration}
                                    onChange={(e) => setNewRate({...newRate, duration: e.target.value})}
                                    className="modal-select"
                                >
                                    <option value="">Select duration</option>
                                    <option value="30 Minutes">30 Minutes</option>
                                    <option value="1 Hour">1 Hour</option>
                                    <option value="2 Hour">2 Hour</option>
                                    <option value="3 Hour">3 Hour</option>
                                    <option value="4 Hour">4 Hour</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Incall Price (€)</label>
                                <input
                                    type="text"
                                    value={newRate.incall}
                                    onChange={(e) => setNewRate({...newRate, incall: e.target.value})}
                                    className="modal-input"
                                    placeholder="300"
                                />
                            </div>
                            <div className="form-group">
                                <label>Outcall Price (€) - Optional</label>
                                <input
                                    type="text"
                                    value={newRate.outcall}
                                    onChange={(e) => setNewRate({...newRate, outcall: e.target.value})}
                                    className="modal-input"
                                    placeholder="350"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setShowAddCityRate(false)}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={handleSaveNewCityRate}>
                                Add Rate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Travel Rate Modal */}
            {editTravelRate && (
                <div className="modal-overlay">
                    <div className="modal small">
                        <div className="modal-header">
                            <h3 className="modal-title">Edit Travel Rate</h3>
                            <button className="modal-close" onClick={() => setEditTravelRate(null)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Duration</label>
                                <select
                                    value={editTravelRate.duration}
                                    onChange={(e) => setEditTravelRate({...editTravelRate, duration: e.target.value})}
                                    className="modal-select"
                                >
                                    <option value="24 Hours">24 Hours</option>
                                    <option value="48 Hours">48 Hours</option>
                                    <option value="72 Hours">72 Hours</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Incall Price (€)</label>
                                <input
                                    type="text"
                                    value={editTravelRate.incall.replace('€', '')}
                                    onChange={(e) => setEditTravelRate({...editTravelRate, incall: `€${e.target.value}`})}
                                    className="modal-input"
                                    placeholder="3000"
                                />
                            </div>
                            <div className="form-group">
                                <label>Outcall Price (€)</label>
                                <input
                                    type="text"
                                    value={editTravelRate.outcall.replace('€', '')}
                                    onChange={(e) => setEditTravelRate({...editTravelRate, outcall: `€${e.target.value}`})}
                                    className="modal-input"
                                    placeholder="3500"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setEditTravelRate(null)}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={handleSaveTravelRate}>
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Travel Rate Modal */}
            {showAddTravelRate && (
                <div className="modal-overlay">
                    <div className="modal small">
                        <div className="modal-header">
                            <h3 className="modal-title">Add Travel Rate</h3>
                            <button className="modal-close" onClick={() => setShowAddTravelRate(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Duration</label>
                                <select
                                    value={newRate.duration}
                                    onChange={(e) => setNewRate({...newRate, duration: e.target.value})}
                                    className="modal-select"
                                >
                                    <option value="">Select duration</option>
                                    <option value="24 Hours">24 Hours</option>
                                    <option value="48 Hours">48 Hours</option>
                                    <option value="72 Hours">72 Hours</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Incall Price (€)</label>
                                <input
                                    type="text"
                                    value={newRate.incall}
                                    onChange={(e) => setNewRate({...newRate, incall: e.target.value})}
                                    className="modal-input"
                                    placeholder="5000"
                                />
                            </div>
                            <div className="form-group">
                                <label>Outcall Price (€) - Optional</label>
                                <input
                                    type="text"
                                    value={newRate.outcall}
                                    onChange={(e) => setNewRate({...newRate, outcall: e.target.value})}
                                    className="modal-input"
                                    placeholder="5500"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setShowAddTravelRate(false)}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={handleSaveNewTravelRate}>
                                Add Rate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3 className="modal-title">Edit User</h3>
                            <button className="modal-close" onClick={() => setEditingUser(null)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-content">
                            <div className="modal-main">
                                <div className="form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text"
                                        value={editingUser.name}
                                        onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                                        placeholder="Enter full name"
                                        className="modal-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        value={editingUser.email}
                                        onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                        placeholder="Enter email address"
                                        className="modal-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Role</label>
                                    <select
                                        value={editingUser.role}
                                        onChange={(e) => setEditingUser({...editingUser, role: Number(e.target.value)})}
                                        className="modal-select"
                                    >
                                        <option value={9}>Admin</option>
                                        <option value={2}>Model</option>
                                        <option value={1}>User</option>
                                    </select>
                                </div>
                                {Number(editingUser.role) === 2 && (
                                    <>
                                        {isLoadingEditModel && <span className="modal-feedback">Loading model details...</span>}
                                        {editingUser.modelDetails && (
                                            <div className="form-group">
                                                <label>Model Name</label>
                                                <input
                                                    type="text"
                                                    value={editingUser.modelDetails.name || ''}
                                                    onChange={(e) => setEditingUser({...editingUser, modelDetails: {...editingUser.modelDetails, name: e.target.value}})}
                                                    placeholder="Enter model name"
                                                    className="modal-input"
                                                />
                                                <label>Profile Slug</label>
                                                <input
                                                    type="text"
                                                    value={editingUser.modelDetails.slug || ''}
                                                    onChange={(e) => setEditingUser({...editingUser, modelDetails: {...editingUser.modelDetails, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}})}
                                                    placeholder="profile-slug"
                                                    className="modal-input"
                                                    maxLength="191"
                                                />
                                                <small>Used in the public profile URL.</small>
                                                <div className="model-link">
                                                    <span className="model-link-label">Connected model: </span>
                                                    <a href={`/model-edit/${encodeURIComponent(getLinkedModelUuid(editingUser))}`} target="_blank" rel="noopener noreferrer" className="model-link-text">
                                                        {editingUser.modelDetails.name}
                                                        <i className="fa-solid fa-external-link-alt model-link-icon"></i>
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={editingUser.status}
                                        onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                                        className="modal-select"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                                {editUserError && <span className="modal-error" role="alert">{editUserError}</span>}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setEditingUser(null)}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={handleSaveEditUser} disabled={isSavingEditUser || isLoadingEditModel}>
                                {isSavingEditUser ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm.show && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3 className="modal-title">Confirm Deletion</h3>
                            <button className="modal-close" onClick={cancelDeleteUser}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <p>Are you sure you want to delete user "{deleteConfirm.user.name}"?</p>
                                <p className="trash-notice"><i className="fa-solid fa-trash-can"></i> This moves the local record to trash, where it can be restored or permanently removed. Connected OAuth and Stripe resources are not deleted.</p>
                                {deleteUserError && <span className="modal-error" role="alert">{deleteUserError}</span>}
                            </div>
                        </div>
                        <div className="modal-footer">
                             {deleteConfirm.user.model ? (
                                <span>This will also move the linked model to trash.</span>
                                ) : ''}
                            <button className="modal-button cancel" onClick={cancelDeleteUser} disabled={isDeletingUser}>
                                Cancel
                            </button>
                            <button className="modal-button delete" onClick={confirmDeleteUser} disabled={isDeletingUser} style={{ background: '#d1242f' }}>
                                {isDeletingUser ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;